import {
  BottomSheet,
  BottomSheetBody,
  BottomSheetHeader,
  Box,
  FadeBorder,
  Image,
  NetworkOption,
  Text,
} from '@frontend/ui-library';
import { createAssetSrc, currencyNameBySymbol } from '@frontend/utils';

import { useState } from 'react';
import { ApiChainSymbols, getNetworkDisplayText } from '../../../../utils';
import PaymentOptionSuggestions from '../PaymentOptionSuggestions';

interface Network {
  network?: string;
  symbol?: string;
  available: boolean;
  reason: string;
  isLowestFeeNetwork?: boolean;
}

export interface NetworkSelectionBottomSheetProps {
  loading: boolean;
  selectedCurrency: string;
  networks: Network[];
  open: boolean;
  onNetworkSelect: (network: string) => void;
  onRequestClose: () => void;
}

interface NetworkGroup {
  label: string;
  groupName: string;
  networks: Network[];
}

const getNetworkDisabledMessage = (
  reason: string
): string => reason || 'Temporarily unavailable';

export default function NetworkSelectionBottomSheet({
  loading,
  networks,
  selectedCurrency,
  open,
  onNetworkSelect,
  onRequestClose,
}: NetworkSelectionBottomSheetProps): JSX.Element {
  const [selectedNetwork, setSelectedNetwork] = useState<string>('');

  const haveLowestFeeNetwork = networks.some(
    (network) => network.isLowestFeeNetwork
  );
  const canGroupByLowestFee = haveLowestFeeNetwork && networks.length > 1;

  const lowestFeeNetworks = networks.filter(
    (network) => network.isLowestFeeNetwork
  );

  const otherNetworks = networks.filter(
    (network) => !network.isLowestFeeNetwork
  );

  const NetworkList = ({
    groupName,
    networks,
  }: {
    networks: Network[];
    groupName?: string;
  }) => (
    <div data-test-id={`network-group-${groupName}`}>
      {networks.map(({ network, symbol, available, reason }, i) => {
        return (
          <Box mt="8" key={symbol}>
            <NetworkOption
              loading={loading && selectedNetwork === network}
              networkName={getNetworkDisplayText(symbol as ApiChainSymbols)}
              description={
                symbol === ApiChainSymbols.SOL && available
                  ? 'Requires a wallet with Solana Pay support'
                  : ''
              }
              disabled={loading || !available}
              onClick={() => {
                onNetworkSelect(symbol || '');
                setSelectedNetwork(network || '');
              }}
              networkIconSrc={symbol && createAssetSrc(symbol, 'networks')}
              infoText={available ? '' : getNetworkDisabledMessage(reason)}
            />
          </Box>
        );
      })}
    </div>
  );

  const NetworkGroup = ({ label, networks, groupName }: NetworkGroup) =>
    networks.length > 0 && (
      <>
        <Text size="tiny-callout" color="royal-700" ml="8" weight="600">
          {label}
        </Text>
        <NetworkList groupName={groupName} networks={networks} />
      </>
    );

  const renderNetworks = () => {
    if (canGroupByLowestFee) {
      return (
        <>
          <NetworkGroup
            groupName="lowest-fee"
            label="Lowest Fees"
            networks={lowestFeeNetworks}
          />
          <Box mt="24">
            <NetworkGroup
              groupName="others"
              label="Other Networks"
              networks={otherNetworks}
            />
          </Box>
        </>
      );
    }

    return <NetworkList networks={networks} />;
  };

  return (
    <BottomSheet
      onRequestClose={onRequestClose}
      open={open}
      data-test-id="network-selection-bottom-sheet"
    >
      <BottomSheetHeader>
        <Image
          width={16}
          height={16}
          src={createAssetSrc(selectedCurrency, 'tickers')}
          alt={selectedCurrency}
        />
        <Text ml="4" color="neutral-900" weight="600" size="tiny">
          {selectedCurrency}
        </Text>
        <Text ml="4" color="neutral-500" size="tiny">
          {currencyNameBySymbol[selectedCurrency]}
        </Text>
      </BottomSheetHeader>
      <FadeBorder ml="24" mr="24" />
      <BottomSheetBody>
        <Text align="center" as="div" color="royal-700" weight="600">
          Select a network
        </Text>
        <Box mt="16">{networks.length > 0 && renderNetworks()}</Box>
        <PaymentOptionSuggestions mt="40" page="networkSelection" />
      </BottomSheetBody>
    </BottomSheet>
  );
}
